import GeometryCollection from 'ol/geom/GeometryCollection';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import type Geometry from 'ol/geom/Geometry';
import type {
  AttributeValue,
  EntityViewLevel,
  LayerModel,
  OperationalAttributeField,
  OriginalAttributes,
  Selection,
  WorkScope,
} from '../core/types';
import { effectiveAttributesForEntity } from './attributes';
import { geodesicArea } from './measurement';
import { workScopeFragmentIds } from './scope';

export interface EntityViewIds {
  readonly fincaId: string;
  readonly suerteId?: string;
  readonly tablonId?: string;
  readonly fragmentId?: string;
}

export interface EntityViewRow {
  readonly id: string;
  readonly level: EntityViewLevel;
  readonly label: string;
  readonly ids: EntityViewIds;
  readonly fragmentIds: readonly string[];
  readonly activeAreaHa: number;
  /** Geodesic source area before edit replay, when sourceModel is available. */
  readonly originalGeometryAreaHa: number;
  /** Imported/declarative area retained by the hierarchy. It is never overwritten by edits. */
  readonly declaredAreaHa: number;
  readonly attributes: OriginalAttributes;
}

export type EntityViewEntity = Pick<EntityViewRow, 'id' | 'level' | 'label' | 'ids' | 'fragmentIds'>;

export const ENTITY_VIEW_LEVELS: readonly EntityViewLevel[] = ['finca', 'suerte', 'tablon', 'fragment'];

export const entityViewParentLevel = (level: EntityViewLevel): EntityViewLevel | null =>
  level === 'fragment' ? 'tablon' : level === 'tablon' ? 'suerte' : level === 'suerte' ? 'finca' : null;

export const entityViewChildLevel = (level: EntityViewLevel): EntityViewLevel | null =>
  level === 'finca' ? 'suerte' : level === 'suerte' ? 'tablon' : level === 'tablon' ? 'fragment' : null;

export const entityViewIdForFragment = (model: LayerModel, fragmentId: string, level: EntityViewLevel): string | null => {
  const fragment = model.fragmentsById.get(fragmentId);
  if (!fragment) return null;
  return level === 'finca' ? fragment.fincaId : level === 'suerte' ? fragment.suerteId : level === 'tablon' ? fragment.tablonId : fragment.id;
};

export function entityViewFragmentIds(model: LayerModel, level: EntityViewLevel, entityId: string): readonly string[] {
  if (level === 'finca') return model.fincas.get(entityId)?.fragmentIds ?? [];
  if (level === 'suerte') return model.suertes.get(entityId)?.fragmentIds ?? [];
  if (level === 'tablon') return model.tablones.get(entityId)?.fragmentIds ?? [];
  return model.fragmentsById.has(entityId) ? [entityId] : [];
}

const scopedFragments = (fragmentIds: readonly string[], allowed?: ReadonlySet<string>): readonly string[] =>
  allowed ? fragmentIds.filter((id) => allowed.has(id)) : fragmentIds;

const geometryArea = (model: LayerModel, fragmentIds: readonly string[]): number => fragmentIds.reduce((sum, fragmentId) => {
  const geometry = model.fragmentsById.get(fragmentId)?.feature.getGeometry();
  return sum + (geometry ? geodesicArea(geometry) / 10_000 : 0);
}, 0);

const sourceArea = (sourceModel: LayerModel | undefined, level: EntityViewLevel, id: string, fallback: number): number => {
  if (!sourceModel) return fallback;
  const fragmentIds = entityViewFragmentIds(sourceModel, level, id);
  return fragmentIds.length ? geometryArea(sourceModel, fragmentIds) : fallback;
};

const rowAttributes = (
  model: LayerModel,
  row: Omit<EntityViewRow, 'attributes'>,
  operationalFields: readonly OperationalAttributeField[],
): OriginalAttributes => {
  const finca = model.fincas.get(row.ids.fincaId);
  const suerte = row.ids.suerteId ? model.suertes.get(row.ids.suerteId) : undefined;
  const tablon = row.ids.tablonId ? model.tablones.get(row.ids.tablonId) : undefined;
  const logical: Record<string, AttributeValue> = {
    Nombre: finca?.name ?? null,
    Finca: finca?.name ?? null,
    Codfinca: finca?.code ?? null,
    'Código de finca': finca?.code ?? null,
    Suerte: suerte?.label ?? null,
    CodSuerte: suerte?.code ?? null,
    'Código de Suerte': suerte?.code ?? null,
    Tablon: tablon?.code ?? null,
    'Área geométrica activa (ha)': row.activeAreaHa,
    'Área geométrica original (ha)': row.originalGeometryAreaHa,
    'Área declarada/original (ha)': row.declaredAreaHa,
    'Fragmentos activos': row.fragmentIds.length,
  };
  return effectiveAttributesForEntity(model, row.fragmentIds, row.ids, operationalFields, logical);
};

/**
 * Deterministic logical rows derived from activeModel. No geometry or source
 * attributes are mutated and the returned order follows the stable hierarchy.
 */
export function entityViewRows(
  model: LayerModel,
  level: EntityViewLevel,
  options: {
    readonly allowedFragmentIds?: ReadonlySet<string>;
    readonly operationalFields?: readonly OperationalAttributeField[];
    readonly sourceModel?: LayerModel | null;
  } = {},
): readonly EntityViewRow[] {
  const fields = options.operationalFields ?? [];
  return entityViewEntities(model, level, options.allowedFragmentIds).map((entity) => {
    const activeAreaHa = geometryArea(model, entity.fragmentIds);
    const logical = level === 'finca' ? model.fincas.get(entity.id) : level === 'suerte' ? model.suertes.get(entity.id) : level === 'tablon' ? model.tablones.get(entity.id) : undefined;
    const fragment = level === 'fragment' ? model.fragmentsById.get(entity.id) : undefined;
    const row: Omit<EntityViewRow, 'attributes'> = {
      ...entity,
      activeAreaHa,
      originalGeometryAreaHa: options.sourceModel === model ? activeAreaHa : sourceArea(options.sourceModel ?? undefined, level, entity.id, activeAreaHa),
      declaredAreaHa: logical?.declaredAreaHa ?? (Number(fragment?.attributes.Area_Ha ?? fragment?.attributes.AreaHa ?? 0) || 0),
    };
    return { ...row, attributes: rowAttributes(model, row, fields) };
  });
}

/** Structural EntityView used by map, selection, classification and labels. */
export function entityViewEntities(model: LayerModel, level: EntityViewLevel, allowed?: ReadonlySet<string>): readonly EntityViewEntity[] {
  const entities: EntityViewEntity[] = [];
  if (level === 'finca') model.fincas.forEach((finca) => {
    const fragmentIds = scopedFragments(finca.fragmentIds, allowed); if (!fragmentIds.length) return;
    entities.push({ id: finca.id, level, label: finca.name, ids: { fincaId: finca.id }, fragmentIds });
  });
  if (level === 'suerte') model.suertes.forEach((suerte) => {
    const fragmentIds = scopedFragments(suerte.fragmentIds, allowed); if (!fragmentIds.length) return;
    entities.push({ id: suerte.id, level, label: `Suerte ${suerte.label}`, ids: { fincaId: suerte.fincaId, suerteId: suerte.id }, fragmentIds });
  });
  if (level === 'tablon') model.tablones.forEach((tablon) => {
    const fragmentIds = scopedFragments(tablon.fragmentIds, allowed); if (!fragmentIds.length) return;
    const suerte = model.suertes.get(tablon.suerteId); if (!suerte) return;
    entities.push({ id: tablon.id, level, label: `Tablón ${tablon.code}`, ids: { fincaId: suerte.fincaId, suerteId: suerte.id, tablonId: tablon.id }, fragmentIds });
  });
  if (level === 'fragment') model.fragments.forEach((fragment) => {
    if (allowed && !allowed.has(fragment.id)) return;
    entities.push({ id: fragment.id, level, label: `Fragmento ${fragment.id.split('/fragment:').at(-1) ?? fragment.id}`, ids: { fincaId: fragment.fincaId, suerteId: fragment.suerteId, tablonId: fragment.tablonId, fragmentId: fragment.id }, fragmentIds: [fragment.id] });
  });
  return entities;
}

/** Lightweight presentation geometry: component-preserving, non-dissolved and transient. */
export function entityPresentationGeometry(model: LayerModel, row: Pick<EntityViewRow, 'fragmentIds'>): Geometry | null {
  const geometries = row.fragmentIds.flatMap((id) => {
    const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
    return geometry ? [geometry.clone()] : [];
  });
  const polygons = geometries.flatMap((geometry) => geometry instanceof Polygon ? [geometry.getCoordinates()] : geometry instanceof MultiPolygon ? geometry.getCoordinates() : []);
  if (polygons.length === geometries.length && polygons.length) return new MultiPolygon(polygons);
  return geometries.length ? new GeometryCollection(geometries) : null;
}

export function entityViewSelection(level: EntityViewLevel, entityId: string): Selection {
  return { kind: level, id: entityId };
}

export function drillDownWorkScope(model: LayerModel, selection: Selection, targetLevel: EntityViewLevel, current: WorkScope): WorkScope {
  if (!selection) return current;
  const fragments = entityViewFragmentIds(model, selection.kind, selection.id);
  if (!fragments.length) return current;
  const fincaIds = new Set<string>(); const suerteIds = new Set<string>(); const tablonIds = new Set<string>();
  fragments.forEach((id) => { const fragment = model.fragmentsById.get(id); if (!fragment) return; fincaIds.add(fragment.fincaId); if (targetLevel !== 'finca') suerteIds.add(fragment.suerteId); if (targetLevel === 'tablon' || targetLevel === 'fragment') tablonIds.add(fragment.tablonId); });
  return { fincaIds: [...fincaIds], suerteIds: [...suerteIds], tablonIds: [...tablonIds] };
}

export const entityViewRowsForScope = (model: LayerModel, level: EntityViewLevel, scope: WorkScope, fields: readonly OperationalAttributeField[] = [], sourceModel?: LayerModel | null): readonly EntityViewRow[] =>
  entityViewRows(model, level, { allowedFragmentIds: workScopeFragmentIds(model, scope), operationalFields: fields, sourceModel });

/** Number of logical entities of `level` represented by a set of visible physical fragments. */
export function countEntitiesForFragments(model: LayerModel, level: EntityViewLevel, fragmentIds: ReadonlySet<string>): number {
  if (level === 'fragment') return fragmentIds.size;
  const ids = new Set<string>();
  fragmentIds.forEach((id) => {
    const fragment = model.fragmentsById.get(id);
    if (fragment) ids.add(level === 'finca' ? fragment.fincaId : level === 'suerte' ? fragment.suerteId : fragment.tablonId);
  });
  return ids.size;
}
